import type { ReactNode } from "react";
import { cx } from "../lib/cx";
import {
  IconAlert,
  IconAlertCircle,
  IconCheckCircle,
  IconCircleDashed,
  IconClock,
  IconInfo,
  IconMinus,
  IconLock,
  IconXCircle,
  Spinner,
} from "./icons";

/**
 * The status tones (lib/statusTone.ts), fixed whatever the brand: good (green), warn (amber: needs attention soon),
 * bad (red: critical), info (blue: neutral, scheduled, running). ok: plain, for healthy rows in lists, so a table of
 * them reads calm. neutral: gray. progress: running work, in info blue with a spinner.
 */
export type Tone = "ok" | "good" | "warn" | "bad" | "info" | "neutral" | "progress";

const toneClass: Record<Tone, string> = {
  ok: "bg-ok-bg text-ok shadow-[inset_0_0_0_1px_var(--hx-ok-line)]",
  good: "bg-good-bg text-good shadow-[inset_0_0_0_1px_var(--hx-good-line)]",
  warn: "bg-warn-bg text-warn shadow-[inset_0_0_0_1px_var(--hx-warn-line)]",
  bad: "bg-bad-bg text-bad shadow-[inset_0_0_0_1px_var(--hx-bad-line)]",
  info: "bg-info-bg text-info shadow-[inset_0_0_0_1px_var(--hx-info-line)]",
  neutral: "bg-panel text-body shadow-[inset_0_0_0_1px_var(--hx-line)]",
  progress: "bg-info-bg text-info shadow-[inset_0_0_0_1px_var(--hx-info-line)]",
};

export type PillIcon = "check" | "x" | "alert" | "warn" | "info" | "clock" | "dash" | "pending" | "spinner" | "lock" | "none";

function PillGlyph({ icon }: { icon: PillIcon }) {
  const s = 13;
  switch (icon) {
    case "check":
      return <IconCheckCircle size={s} strokeWidth={2} />;
    case "x":
      return <IconXCircle size={s} strokeWidth={2} />;
    case "alert":
      return <IconAlertCircle size={s} strokeWidth={2} />;
    case "warn":
      return <IconAlert size={s} strokeWidth={2} />;
    case "clock":
      return <IconClock size={s} strokeWidth={2} />;
    case "info":
      return <IconInfo size={s} strokeWidth={2} />;
    case "dash":
      return <IconMinus size={s} strokeWidth={2} />;
    case "pending":
      return <IconCircleDashed size={s} strokeWidth={2} />;
    case "spinner":
      return <Spinner size={12} />;
    case "lock":
      return <IconLock size={s} strokeWidth={2} />;
    default:
      return null;
  }
}

const defaultIcon: Record<Tone, PillIcon> = {
  ok: "check",
  good: "check",
  warn: "warn",
  bad: "alert",
  info: "info",
  neutral: "dash",
  progress: "spinner",
};

/**
 * Status in a short word with its mark, never color alone. Warning, critical, info and running work sit on their
 * status tint with an icon; good (and ok) stays light, a green dot and the word, so a list of healthy rows reads
 * calm rather than as a wall of green.
 */
export function StatusPill({
  tone,
  children,
  icon,
  className,
  title,
}: {
  tone: Tone;
  children: ReactNode;
  icon?: PillIcon;
  className?: string;
  title?: string;
}) {
  if ((tone === "good" || tone === "ok") && (icon === undefined || icon === "check")) {
    return (
      <span title={title} className={cx("inline-flex h-6 max-w-full items-center gap-1.5 font-sans text-[12.5px] leading-none font-medium tracking-normal whitespace-nowrap text-fg", className)}>
        <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-good-fill shadow-[0_0_0_3px_var(--hx-good-bg)]" />
        <span className="truncate">{children}</span>
      </span>
    );
  }
  return (
    <span
      title={title}
      className={cx(
        "inline-flex h-6 max-w-full items-center gap-1.5 rounded-full pr-2.5 pl-2 font-sans text-[12px] font-semibold leading-none tracking-normal whitespace-nowrap",
        toneClass[tone],
        className,
      )}
    >
      <PillGlyph icon={icon ?? defaultIcon[tone]} />
      <span className="truncate">{children}</span>
    </span>
  );
}

/** A plain label chip, for kinds and tags rather than status. */
export function Badge({ children, className, mono }: { children: ReactNode; className?: string; mono?: boolean }) {
  return (
    <span
      className={cx(
        "inline-flex h-5.5 items-center rounded-md bg-panel px-1.5 font-medium tracking-normal text-body shadow-[inset_0_0_0_1px_var(--hx-line)]",
        // One family and size or the other: with both classes, the stylesheet's order picks (font-sans won, so mono never showed).
        mono ? "font-mono text-[11px]" : "font-sans text-[11.5px]",
        className,
      )}
    >
      {children}
    </span>
  );
}
